/**
 * CR500 Phase 2B: Details Presentation Types
 * 
 * Types for controlling how deal details are presented (flip, modal, sidePanel, inlineExpand).
 * Layout Preset controls presentation mode; DealCardSpec v2 controls content.
 */

// ================================================================
// PRESENTATION MODES
// ================================================================

/**
 * Available presentation modes for deal details.
 * - flip: Card flips to reveal details on back (current behavior)
 * - modal: Details open in a centered modal dialog
 * - sidePanel: Details open in a right-side drawer/panel
 * - inlineExpand: Details expand inline below the card
 */
export type DetailsPresentationMode = "flip" | "modal" | "sidePanel" | "inlineExpand";

/**
 * Size variants for modal and sidePanel modes.
 */
export type DetailsOverlaySize = "sm" | "md" | "lg";

// ================================================================
// ENTRY BUTTON CONFIG
// ================================================================

/**
 * Configuration for the details entry button (the trigger that opens details).
 */
export interface DetailsEntryConfig {
  /** Button label text */
  label: string;
  /** Optional icon name (from lucide-react) */
  icon?: string;
  /** Button position on the card */
  position?: "bottom-right" | "bottom-left" | "bottom-center";
  /** Button style variant */
  variant?: "primary" | "secondary" | "ghost";
}

export const DEFAULT_DETAILS_ENTRY: DetailsEntryConfig = {
  label: "Details",
  position: "bottom-right",
  variant: "primary",
};

// ================================================================
// MODE-SPECIFIC CONFIGS
// ================================================================

/**
 * Configuration specific to modal presentation mode.
 */
export interface ModalPresentationConfig {
  /** Modal size */
  size: DetailsOverlaySize;
  /** Show close button */
  showCloseButton?: boolean;
  /** Allow backdrop click to close */
  closeOnBackdropClick?: boolean;
}

export const DEFAULT_MODAL_CONFIG: ModalPresentationConfig = {
  size: "md",
  showCloseButton: true,
  closeOnBackdropClick: true,
};

/**
 * Configuration specific to sidePanel presentation mode.
 */
export interface SidePanelPresentationConfig {
  /** Panel width */
  width: DetailsOverlaySize;
  /** Panel position */
  position?: "right" | "left";
  /** Show overlay behind panel */
  showOverlay?: boolean;
}

export const DEFAULT_SIDE_PANEL_CONFIG: SidePanelPresentationConfig = {
  width: "md",
  position: "right",
  showOverlay: true,
};

/**
 * Configuration specific to inlineExpand presentation mode.
 */
export interface InlineExpandPresentationConfig {
  /** Only one card can be expanded at a time */
  expandSingleOpen: boolean;
  /** Animation duration in ms */
  animationDuration?: number;
}

export const DEFAULT_INLINE_EXPAND_CONFIG: InlineExpandPresentationConfig = {
  expandSingleOpen: true,
  animationDuration: 300,
};

// ================================================================
// FULL PRESENTATION CONFIG
// ================================================================

/**
 * Complete details presentation configuration.
 * Stored in LayoutJson.dealCardUX.detailsPresentation.
 */
export interface DetailsPresentationConfig {
  /** The presentation mode to use */
  mode: DetailsPresentationMode;
  /** Entry button configuration */
  entry: DetailsEntryConfig;
  /** Modal-specific config (when mode === "modal") */
  modal?: ModalPresentationConfig;
  /** SidePanel-specific config (when mode === "sidePanel") */
  sidePanel?: SidePanelPresentationConfig;
  /** InlineExpand-specific config (when mode === "inlineExpand") */
  inlineExpand?: InlineExpandPresentationConfig;
}

export const DEFAULT_DETAILS_PRESENTATION: DetailsPresentationConfig = {
  mode: "flip",
  entry: DEFAULT_DETAILS_ENTRY,
  modal: DEFAULT_MODAL_CONFIG,
  sidePanel: DEFAULT_SIDE_PANEL_CONFIG,
  inlineExpand: DEFAULT_INLINE_EXPAND_CONFIG,
};

// ================================================================
// LEGACY MIGRATION HELPERS
// ================================================================

/**
 * Derive DetailsPresentationConfig from legacy detailsMode.
 * Used during migration when detailsPresentation is not present.
 */
export function deriveDetailsPresentationFromLegacy(
  legacyMode: "flip" | "expand" | "panel" | undefined,
  legacyExpandSingleOpen?: boolean
): DetailsPresentationConfig {
  // Map legacy mode names to new mode names
  const modeMap: Record<string, DetailsPresentationMode> = {
    flip: "flip",
    expand: "inlineExpand",
    panel: "sidePanel",
  };
  
  const mode = legacyMode ? (modeMap[legacyMode] ?? "flip") : "flip";
  
  return {
    mode,
    entry: DEFAULT_DETAILS_ENTRY,
    modal: DEFAULT_MODAL_CONFIG,
    sidePanel: DEFAULT_SIDE_PANEL_CONFIG,
    inlineExpand: {
      ...DEFAULT_INLINE_EXPAND_CONFIG,
      expandSingleOpen: legacyExpandSingleOpen ?? true,
    },
  };
}

// ================================================================
// UI LABELS
// ================================================================

export const PRESENTATION_MODE_LABELS: Record<DetailsPresentationMode, { 
  label: string; 
  description: string;
}> = {
  flip: { 
    label: "Flip", 
    description: "Card flips to reveal details on back" 
  },
  modal: { 
    label: "Modal", 
    description: "Details open in a centered dialog" 
  },
  sidePanel: { 
    label: "Side Panel", 
    description: "Details slide in from the side" 
  },
  inlineExpand: { 
    label: "Inline Expand", 
    description: "Details expand below the card" 
  },
};

export const OVERLAY_SIZE_LABELS: Record<DetailsOverlaySize, string> = {
  sm: "Small",
  md: "Medium",
  lg: "Large",
};
